// src/pages/pentest/dashboard/DarkWebFindings.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Divider,
  TablePagination,
  Tooltip,
  Drawer,
  IconButton,
  Stack,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import StorageIcon from '@mui/icons-material/Storage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import Nav from './nav';
import { alpha } from '@mui/material/styles';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

const FAIL_RED = '#C62828';
const LEAK_GREEN = '#2E7D32';
const SLATE_TEXT = '#607086';
const PAGE_BG = '#F5F8FC';

const shellCardSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.1),
  boxShadow: `0 14px 28px ${alpha(PRIMARYCOLOR, 0.06)}`,
  backgroundColor: '#ffffff'
};

const isPresent = (v) => !!v && v !== 'None' && String(v).trim() !== '';

const truncateMiddle = (s, left = 6, right = 4, max = 24) => {
  if (!s) return '';
  const str = String(s);
  if (str.length <= max) return str;
  return `${str.slice(0, left)}...${str.slice(-right)}`;
};

const stripHtml = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const formatNumber = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n ?? '');
  return x.toLocaleString();
};

const monthShort = (isoDate) => {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short' });
};

const yearNum = (isoDate) => {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return String(d.getUTCFullYear());
};

const sortByBreachDateDesc = (a, b) =>
  String(b?.BreachDate || '').localeCompare(String(a?.BreachDate || ''));

const SummaryCard = ({ label, value, helper, Icon, accent = PRIMARYCOLOR }) => (
  <Card sx={{ ...shellCardSx, height: '100%', borderTop: `3px solid ${accent}` }}>
    <Box sx={{ p: 2.2 }}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            bgcolor: alpha(accent, 0.12),
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.45, fontWeight: 800, color: '#10233B', lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.7 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </Card>
);

const ResultChip = ({ label, color, icon, outline = false }) => (
  <Chip
    icon={icon}
    label={label}
    variant={outline ? 'outlined' : 'filled'}
    sx={{
      fontWeight: 800,
      bgcolor: outline ? '#ffffff' : alpha(color, 0.1),
      color,
      borderColor: alpha(color, 0.28)
    }}
  />
);

const DarkWebFindings = () => {
  const [leaksData, setLeaksData] = useState([]);
  const [hibpRows, setHibpRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [onlyClear, setOnlyClear] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRpp] = useState(10);

  // Drawer / timeline state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEmail, setDrawerEmail] = useState('');
  const [drawerBreaches, setDrawerBreaches] = useState([]);

  const { pentest_id } = useParams();
  const navigate = useNavigate();

  // Fetch dark web leak data + HIBP breaches from backend
  useEffect(() => {
    const fetchLeaks = async () => {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }
      try {
        const resp = await fetch(`${BASEURL}/pentests/darkweb/leaks?pentest_id=${pentest_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
        const data = await resp.json();
        if (data.error) {
          setErr(data.message || 'Failed to load dark web leaks.');
        } else {
          setLeaksData(Array.isArray(data.leaks) ? data.leaks : []);
          setHibpRows(Array.isArray(data.hibp_breaches) ? data.hibp_breaches : []);
        }
      } catch (e) {
        console.error(e);
        setErr('Network error fetching dark web leaks.');
      } finally {
        setLoading(false);
      }
    };
    fetchLeaks();
  }, [pentest_id, navigate]);

  // Build a map: email -> breaches[] (parsed from HIBP "data" field)
  const hibpByEmail = useMemo(() => {
    const m = new Map();

    for (const r of hibpRows) {
      const email = (r?.email || '').toLowerCase();
      if (!isPresent(email)) continue;

      let parsed = [];
      try {
        parsed = JSON.parse(r?.data || '[]');
        if (!Array.isArray(parsed)) parsed = [];
      } catch {
        parsed = [];
      }

      if (!m.has(email)) m.set(email, []);
      m.get(email).push(...parsed);
    }

    // De-dupe breaches by Name/Title
    for (const [email, arr] of m.entries()) {
      const byName = new Map();
      for (const b of arr) {
        const key = b?.Name || b?.Title || '';
        if (!isPresent(key)) continue;
        if (!byName.has(key)) byName.set(key, b);
      }
      m.set(email, [...byName.values()]);
    }

    return m;
  }, [hibpRows]);

  const openDrawerForEmail = (email) => {
    const e = (email || '').toLowerCase();
    setDrawerEmail(e);
    setDrawerBreaches(hibpByEmail.get(e) || []);
    setDrawerOpen(true);
  };

  // Group findings by email; aggregate passwords + databases (leaks + HIBP)
  const grouped = useMemo(() => {
    const byEmail = new Map();

    // 1) leaks
    for (const leak of leaksData) {
      const email = (leak.email || '').toLowerCase();
      if (!isPresent(email)) continue;

      if (!byEmail.has(email)) {
        byEmail.set(email, {
          email,
          count: 0,
          passwords: new Set(),
          leakDatabases: new Map(), // name -> count
          hibpBreaches: [],
        });
      }

      const g = byEmail.get(email);
      g.count += 1;

      if (isPresent(leak.password)) g.passwords.add(String(leak.password));

      if (isPresent(leak.database_name)) {
        const db = String(leak.database_name);
        g.leakDatabases.set(db, (g.leakDatabases.get(db) || 0) + 1);
      }
    }

    // 2) ensure emails that exist only in HIBP still show up
    for (const [email] of hibpByEmail.entries()) {
      if (!byEmail.has(email)) {
        byEmail.set(email, {
          email,
          count: 0,
          passwords: new Set(),
          leakDatabases: new Map(),
          hibpBreaches: [],
        });
      }
    }

    // 3) attach hibp breaches per email
    for (const [email, g] of byEmail.entries()) {
      g.hibpBreaches = hibpByEmail.get(email) || [];
    }

    // 4) shape rows
    const rows = [...byEmail.values()].map((g) => {
      const leakDbs = [...g.leakDatabases.entries()].map(([name, count]) => ({
        name,
        count,
        source: 'leak',
      }));

      const leakNameSet = new Set(leakDbs.map((d) => d.name));
      const hibpDbs = (g.hibpBreaches || [])
        .map((b) => b?.Name || b?.Title)
        .filter((n) => isPresent(n))
        // if a breach also exists in leaks, keep it as "leak" (green) by NOT duplicating
        .filter((n) => !leakNameSet.has(n))
        .map((name) => ({ name: String(name), count: null, source: 'hibp' }));

      const databases = [...leakDbs, ...hibpDbs];

      return {
        email: g.email,
        count: g.count,
        passwords: [...g.passwords],
        databases,
        hibpBreaches: g.hibpBreaches || [],
      };
    });

    rows.sort((a, b) => (b.count - a.count) || (b.databases.length - a.databases.length));
    return rows;
  }, [leaksData, hibpByEmail]);

  // Filtering & search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = grouped;

    if (onlyClear) rows = rows.filter((r) => r.passwords.length > 0);

    if (q) {
      rows = rows.filter((r) => {
        if (r.email.includes(q)) return true;
        if (r.databases.some((d) => d.name.toLowerCase().includes(q))) return true;
        if ((r.hibpBreaches || []).some((b) => String(b?.Name || b?.Title || '').toLowerCase().includes(q))) return true;
        return false;
      });
    }
    return rows;
  }, [grouped, search, onlyClear]);

  const totalClearEntries = useMemo(
    () => leaksData.reduce((acc, l) => acc + (isPresent(l.password) ? 1 : 0), 0),
    [leaksData]
  );

  const usersWithClear = useMemo(
    () => grouped.reduce((acc, row) => acc + (row.passwords.length > 0 ? 1 : 0), 0),
    [grouped]
  );

  const uniqueHibpEmails = useMemo(() => hibpByEmail.size, [hibpByEmail]);

  const totalHibpBreaches = useMemo(() => {
    let n = 0;
    for (const arr of hibpByEmail.values()) n += (arr?.length || 0);
    return n;
  }, [hibpByEmail]);

  const distinctSourceCount = useMemo(() => {
    const names = new Set();
    grouped.forEach((row) => row.databases.forEach((db) => names.add(db.name)));
    return names.size;
  }, [grouped]);

  const drawerPwnCount = useMemo(
    () => drawerBreaches.reduce((acc, breach) => acc + (Number(breach?.PwnCount) || 0), 0),
    [drawerBreaches]
  );

  const newestDrawerDate = useMemo(() => {
    const sorted = drawerBreaches.slice().sort(sortByBreachDateDesc);
    return sorted[0]?.BreachDate || '';
  }, [drawerBreaches]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnack({ open: true, msg: 'Copied to clipboard' });
    } catch {
      setSnack({ open: true, msg: 'Copy failed' });
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRpp(parseInt(e.target.value, 10));
    setPage(0);
  };

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const summaryCards = [
    {
      label: 'Exposed Emails',
      value: formatNumber(grouped.length),
      helper: 'Unique identities represented across the combined leak and breach-history datasets.',
      Icon: EmailIcon,
      accent: PRIMARYCOLOR
    },
    {
      label: 'Leak Rows',
      value: formatNumber(leaksData.length),
      helper: 'Raw dark web leak records currently associated with this monitoring engagement.',
      Icon: StorageIcon,
      accent: PRIMARYCOLOR
    },
    {
      label: 'Cleartext Passwords',
      value: formatNumber(totalClearEntries),
      helper: `${formatNumber(usersWithClear)} users currently have at least one cleartext exposure.`,
      Icon: LockIcon,
      accent: FAIL_RED
    },
    {
      label: 'HIBP Breaches',
      value: formatNumber(totalHibpBreaches),
      helper: `${formatNumber(uniqueHibpEmails)} monitored emails currently have linked breach-history records.`,
      Icon: InfoOutlinedIcon,
      accent: '#2563EB'
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: PAGE_BG }}>
      <Nav />

      {/* Right-side Drawer Timeline */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 620 },
            bgcolor: '#F7FAFE',
            backgroundImage: `radial-gradient(circle at top right, ${alpha(PRIMARYCOLOR, 0.1)} 0%, transparent 38%)`,
            borderLeft: `1px solid ${alpha(PRIMARYCOLOR, 0.1)}`,
          },
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 1.5 }}>
          <InfoOutlinedIcon sx={{ color: PRIMARYCOLOR, mt: 0.4 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
              Breach Timeline
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.55, fontWeight: 800, color: '#10233B', wordBreak: 'break-all' }}>
              {drawerEmail || 'No email selected'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ ml: 'auto', color: SLATE_TEXT }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={1} sx={{ px: 2.5, pb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip label={`${formatNumber(drawerBreaches.length)} breach records`} sx={{ fontWeight: 800, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
          <Chip label={`${formatNumber(drawerPwnCount)} total pwned accounts`} sx={{ fontWeight: 800, bgcolor: alpha(FAIL_RED, 0.08), color: FAIL_RED }} />
          {newestDrawerDate ? (
            <Chip label={`Newest event ${monthShort(newestDrawerDate)} ${yearNum(newestDrawerDate)}`} sx={{ fontWeight: 800, bgcolor: alpha('#2563EB', 0.08), color: '#2563EB' }} />
          ) : null}
        </Stack>

        <Divider sx={{ mb: 2, borderColor: alpha(PRIMARYCOLOR, 0.1) }} />

        {!drawerBreaches.length ? (
          <Alert severity="info">No HIBP breach details available for this email.</Alert>
        ) : (
          <Box sx={{ px: 2.5, pb: 2 }}>
            {/* timeline wrapper */}
            <Box
              sx={{
                position: 'relative',
                pl: 7.5,
                pr: 0.5,
              }}
            >
              {/* vertical line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 28,
                  top: 12,
                  bottom: 12,
                  width: 2,
                  bgcolor: alpha(PRIMARYCOLOR, 0.18),
                  borderRadius: 999,
                }}
              />

              {drawerBreaches
                .slice()
                .sort(sortByBreachDateDesc)
                .map((b, idx) => {
                  const name = b?.Name || b?.Title || 'Unknown';
                  const title = b?.Title || name;
                  const date = b?.BreachDate || '';
                  const pwn = formatNumber(b?.PwnCount);
                  const classes = Array.isArray(b?.DataClasses) ? b.DataClasses : [];
                  const desc = stripHtml(b?.Description || '');
                  const logo = b?.LogoPath;

                  return (
                    <Box key={`${name}-${idx}`} sx={{ position: 'relative', mb: 2.25 }}>
                      {/* date bubble */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -52,
                          top: 18,
                          width: 52,
                          height: 52,
                          borderRadius: '50%',
                          bgcolor: PRIMARYCOLOR,
                          color: '#fff',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 12px 22px ${alpha(PRIMARYCOLOR, 0.18)}`,
                          border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                          zIndex: 2,
                        }}
                      >
                        <Typography sx={{ fontSize: 12, fontWeight: 900, lineHeight: 1 }}>
                          {monthShort(date)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 900, lineHeight: 1 }}>
                          {yearNum(date)}
                        </Typography>
                      </Box>

                      {/* content card */}
                      <Card
                        sx={{
                          p: 1.75,
                          borderRadius: 3,
                          bgcolor: '#ffffff',
                          border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                          boxShadow: `0 14px 24px ${alpha(PRIMARYCOLOR, 0.08)}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                          {isPresent(logo) ? (
                            <Box
                              component="img"
                              src={logo}
                              alt={`${title} logo`}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                border: `1px solid ${alpha(PRIMARYCOLOR, 0.12)}`,
                                objectFit: 'contain',
                                bgcolor: '#ffffff',
                                p: 0.75,
                              }}
                            />
                          ) : (
                            <Avatar sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(PRIMARYCOLOR, 0.1), color: PRIMARYCOLOR }}>
                              <PublicRoundedIcon sx={{ fontSize: 20 }} />
                            </Avatar>
                          )}

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontWeight: 900, lineHeight: 1.2, color: '#10233B' }}>
                                {title}
                              </Typography>

                              <Chip
                                size="small"
                                label={`Pwned: ${pwn}`}
                                sx={{
                                  ml: 'auto',
                                  bgcolor: alpha(PRIMARYCOLOR, 0.12),
                                  color: PRIMARYCOLOR,
                                  fontWeight: 900,
                                  border: `1px solid ${alpha(PRIMARYCOLOR, 0.25)}`,
                                }}
                              />
                            </Box>

                            {isPresent(b?.Domain) ? (
                              <Typography
                                variant="caption"
                                sx={{ color: SLATE_TEXT, display: 'block', mt: 0.25 }}
                              >
                                Domain: {b.Domain}
                              </Typography>
                            ) : null}

                            {classes.length ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                {classes.map((c) => (
                                  <Chip
                                    key={`${name}-${c}`}
                                    size="small"
                                    label={c}
                                    variant="outlined"
                                    sx={{
                                      fontWeight: 800,
                                      color: '#20344B',
                                      borderColor: alpha(PRIMARYCOLOR, 0.12),
                                      bgcolor: alpha(PRIMARYCOLOR, 0.05)
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : null}

                            {isPresent(desc) ? (
                              <Typography
                                variant="body2"
                                sx={{ mt: 1.1, whiteSpace: 'pre-wrap', color: SLATE_TEXT, lineHeight: 1.75 }}
                              >
                                {desc}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </Card>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        )}
      </Drawer>

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
              Exposure Findings
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>
              Dark Web Leak Findings
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.95, maxWidth: 900, color: SLATE_TEXT, lineHeight: 1.8 }}>
              Review exposed identities, recovered cleartext credentials, and linked breach sources across both direct leak evidence and enriched breach-history records.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip label={`${formatNumber(filtered.length)} visible emails`} sx={{ fontWeight: 800, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
            <Chip label={`${formatNumber(distinctSourceCount)} breach sources`} sx={{ fontWeight: 800, bgcolor: alpha('#2563EB', 0.08), color: '#2563EB' }} />
            <Chip label={`${formatNumber(usersWithClear)} with cleartext`} sx={{ fontWeight: 800, bgcolor: alpha(FAIL_RED, 0.08), color: FAIL_RED }} />
          </Stack>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.label}>
              <SummaryCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* filters */}
        <Card sx={{ ...shellCardSx, mb: 2.5 }}>
          <Box sx={{ p: 2.2 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
              Filters
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.6, fontWeight: 800, color: '#10233B' }}>
              Narrow the current findings set
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <TextField
              size="small"
              placeholder="Search email or breach source..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 360 } }}
            />
              <Chip
                icon={<LockIcon />}
                label="Has cleartext"
                onClick={() => {
                  setOnlyClear(!onlyClear);
                  setPage(0);
                }}
                variant={onlyClear ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 800,
                  bgcolor: onlyClear ? alpha(FAIL_RED, 0.1) : '#ffffff',
                  color: onlyClear ? FAIL_RED : '#20344B',
                  borderColor: alpha(FAIL_RED, 0.25)
                }}
              />
              {(search || onlyClear) ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setSearch('');
                    setOnlyClear(false);
                    setPage(0);
                  }}
                  sx={{ fontWeight: 800 }}
                >
                  Clear filters
                </Button>
              ) : null}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1.8, flexWrap: 'wrap', rowGap: 1 }}>
              <ResultChip label="Green tags are direct leak sources" color={LEAK_GREEN} />
              <ResultChip label="Blue tags are HIBP breach sources" color={PRIMARYCOLOR} />
            </Stack>
          </Box>
        </Card>

        <Card sx={{ ...shellCardSx, p: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 2.2, pb: 1.4 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
              Findings Table
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.6, fontWeight: 800, color: '#10233B' }}>
              Identity-level exposure inventory
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: SLATE_TEXT, lineHeight: 1.75 }}>
              Each row consolidates direct leak evidence, recovered passwords, and enriched breach-history records tied to an exposed email address.
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: PRIMARYCOLOR, color: '#ffffff', fontWeight: 800, letterSpacing: '0.03em', borderBottom: 'none' } }}>
                  <TableCell sx={{ minWidth: 260 }}>Identity</TableCell>
                  <TableCell sx={{ minWidth: 420 }}>
                    Passwords&nbsp;
                    <Typography component="span" variant="caption" sx={{ opacity: 0.8, color: '#fff' }}>
                      (cleartext)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 460 }}>
                    Breach Sources&nbsp;
                    <Typography component="span" variant="caption" sx={{ opacity: 0.8, color: '#fff' }}>
                      (leaks + HIBP)
                    </Typography>
                  </TableCell>

                  {/* ✅ NEW: Actions column */}
                  <TableCell sx={{ width: 120 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Box sx={{ py: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ color: SLATE_TEXT }}>Loading findings...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : err ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Alert severity="error">{err}</Alert>
                    </TableCell>
                  </TableRow>
                ) : pageRows.length ? (
                  pageRows.map((row) => (
                    <TableRow
                      key={row.email}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { background: alpha(PRIMARYCOLOR, 0.016) },
                        '& td': {
                          verticalAlign: 'top',
                          borderBottomColor: alpha(PRIMARYCOLOR, 0.06)
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(PRIMARYCOLOR, 0.1), color: PRIMARYCOLOR }}>
                            <EmailIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.8 }}>
                              <Typography sx={{ fontWeight: 800, color: '#10233B', wordBreak: 'break-all' }}>{row.email}</Typography>
                              <Tooltip title="Copy email">
                                <IconButton size="small" onClick={() => handleCopy(row.email)}>
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            <Stack direction="row" spacing={0.8} sx={{ mt: 0.8, flexWrap: 'wrap', rowGap: 0.8 }}>
                              <ResultChip label={`${formatNumber(row.count)} leak rows`} color={PRIMARYCOLOR} />
                              <ResultChip label={`${formatNumber(row.passwords.length)} cleartext`} color={FAIL_RED} />
                              <ResultChip label={`${formatNumber(row.hibpBreaches?.length || 0)} HIBP`} color="#2563EB" />
                            </Stack>
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: SLATE_TEXT, display: 'block', mt: 0.8 }}>
                          Consolidated identity view across direct leak evidence and enriched breach-history records.
                        </Typography>
                      </TableCell>

                      {/* Cleartext Passwords */}
                      <TableCell>
                        {row.passwords.length ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} onClick={(e) => e.stopPropagation()}>
                            {row.passwords.map((pw, i) => (
                              <Tooltip key={i} title={pw}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  icon={<LockIcon sx={{ fontSize: 16 }} />}
                                  label={truncateMiddle(pw, 8, 4, 22)}
                                  onClick={() => handleCopy(pw)}
                                  sx={{
                                    borderColor: alpha('#f44336', 0.35),
                                    color: '#f44336',
                                    fontWeight: 700,
                                    '& .MuiChip-label': { px: 1 },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
                            No cleartext password entries were recovered for this identity.
                          </Typography>
                        )}
                      </TableCell>

                      {/* Databases */}
                      <TableCell>
                        {row.databases.length ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} onClick={(e) => e.stopPropagation()}>
                            {row.databases
                              .slice()
                              .sort((a, b) => {
                                const s = a.source === b.source ? 0 : a.source === 'leak' ? -1 : 1;
                                if (s !== 0) return s;
                                return String(a.name).localeCompare(String(b.name));
                              })
                              .map((d) => {
                                const isLeak = d.source === 'leak';
                                const label = isLeak && d.count ? `${d.name} (${d.count})` : d.name;

                                return (
                                  <Tooltip key={`${row.email}-${d.source}-${d.name}`} title={isLeak ? 'From leaks' : 'From HIBP'}>
                                    <Chip
                                      size="small"
                                      label={label}
                                      sx={{
                                        bgcolor: isLeak ? alpha(LEAK_GREEN, 0.10) : alpha(PRIMARYCOLOR, 0.10),
                                        color: isLeak ? LEAK_GREEN : PRIMARYCOLOR,
                                        fontWeight: 800,
                                        border: `1px solid ${
                                          isLeak ? alpha(LEAK_GREEN, 0.30) : alpha(PRIMARYCOLOR, 0.30)
                                        }`,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
                            No named breach sources are currently attached to this identity.
                          </Typography>
                        )}
                      </TableCell>

                      {/* ✅ Actions */}
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<TimelineIcon />}
                          onClick={() => openDrawerForEmail(row.email)}
                          sx={{
                            fontWeight: 800,
                            borderColor: alpha(PRIMARYCOLOR, 0.24),
                            color: PRIMARYCOLOR,
                            backgroundColor: alpha(PRIMARYCOLOR, 0.04)
                          }}
                        >
                          Timeline
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 7 }}>
                      <Typography sx={{ fontWeight: 800, color: '#10233B' }}>
                        No findings match the current filters.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>
                        Try clearing the search or showing all exposed users again.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ borderColor: alpha(PRIMARYCOLOR, 0.08) }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', p: 1.2 }}>
            <Typography variant="caption" sx={{ color: SLATE_TEXT, pl: 0.8 }}>
              Showing {formatNumber(pageRows.length)} of {formatNumber(filtered.length)} exposed identities
            </Typography>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </Card>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        onClose={() => setSnack({ open: false, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ fontWeight: 700 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DarkWebFindings;
